/**
 * Hook for fetching and managing Lambda inventory and Brazil EOL data from DynamoDB.
 * Handles loading states, error states, and data transformation.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Account, LambdaFunction, Pipeline, BrazilEOLInterpreterData } from '../types';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';
import { LAMBDA_CONFIG, getEnabledConfigs } from '../config';

export const useInventoryData = () => {
  // Resource type selection (tabs)
  const [activeResourceType, setActiveResourceType] = useState<string>('lambda');
  const enabledConfigs = getEnabledConfigs();
  const currentConfig = enabledConfigs.find((c) => c.id === activeResourceType) || LAMBDA_CONFIG;

  // Lambda inventory state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);

  // Brazil EOL Interpreter VS data
  const [brazilEOLData, setBrazilEOLData] = useState<BrazilEOLInterpreterData | null>(null);
  const [isBrazilEOLLoading, setIsBrazilEOLLoading] = useState(true);
  const [brazilEOLError, setBrazilEOLError] = useState<string | null>(null);

  // Fetch Lambda inventory on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setDataError(null);
      try {
        console.log('[Upgrade Medic] Fetching Lambda inventory from DynamoDB...');
        const data = await UpgradeMedicDataService.getLambdaInventory();
        console.log(`[Upgrade Medic] Loaded ${data.length} accounts from DynamoDB`);
        setAccounts(data);
      } catch (error) {
        console.error('[Upgrade Medic] Failed to load from DynamoDB:', error);
        setDataError('Failed to load Lambda inventory. Please check your permissions and try again.');
        setAccounts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch Brazil EOL data on mount
  useEffect(() => {
    const fetchBrazilEOLData = async () => {
      setIsBrazilEOLLoading(true);
      setBrazilEOLError(null);
      try {
        console.log('[Upgrade Medic] Fetching Brazil EOL Interpreter data from DynamoDB...');
        const data = await UpgradeMedicDataService.getVersionSetDetails();
        console.log(
          `[Upgrade Medic] Loaded ${data.totalVersionsets} versionsets, CPython EOL: ${data.cpythonEOLCount}, NodeJS EOL: ${data.nodejsEOLCount}`
        );
        setBrazilEOLData(data);
      } catch (error) {
        console.error('[Upgrade Medic] Failed to load Brazil EOL data from DynamoDB:', error);
        setBrazilEOLError('Failed to load VersionSet data. Please check your permissions and try again.');
        setBrazilEOLData(null);
      } finally {
        setIsBrazilEOLLoading(false);
      }
    };
    fetchBrazilEOLData();
  }, []);

  // Pre-compute all derived data once when accounts change
  const precomputedData = useMemo(() => {
    const allLambdas: LambdaFunction[] = [];
    const pipelinesGroupedByName = new Map<string, Array<{ pipeline: Pipeline; accountName: string; accountId: string }>>();
    const eolLambdaIds = new Set<string>();
    const eolVersionsSet = new Set(currentConfig.eolVersions);
    let totalPipelineInstances = 0;

    accounts.forEach((account) => {
      account.pipelines.forEach((pipeline) => {
        totalPipelineInstances++;
        const pipelineEntry = { pipeline, accountName: account.name, accountId: account.id };
        const existing = pipelinesGroupedByName.get(pipeline.name) || [];
        existing.push(pipelineEntry);
        pipelinesGroupedByName.set(pipeline.name, existing);

        pipeline.lambdas.forEach((lambda) => {
          allLambdas.push(lambda);
          if (eolVersionsSet.has(lambda.runtime)) {
            eolLambdaIds.add(lambda.id);
          }
        });
      });
    });

    return {
      allLambdas,
      pipelinesGroupedByName,
      eolLambdaIds,
      totalLambdas: allLambdas.length,
      totalPipelines: pipelinesGroupedByName.size,
      totalPipelineInstances,
      eolCount: eolLambdaIds.size
    };
  }, [accounts, currentConfig.eolVersions]);

  // Pre-computed EOL data grouped by runtime → pipeline
  const precomputedEolData = useMemo(() => {
    const eolByRuntimeAndPipeline = new Map<string, Map<string, LambdaFunction[]>>();
    const approachingByRuntimeAndPipeline = new Map<string, Map<string, LambdaFunction[]>>();
    const eolSet = new Set(currentConfig.eolVersions);
    const approachingSet = new Set(currentConfig.approachingEolVersions || []);

    accounts.forEach((account) => {
      account.pipelines.forEach((pipeline) => {
        pipeline.lambdas.forEach((lambda) => {
          const addToMap = (map: Map<string, Map<string, LambdaFunction[]>>, runtime: string) => {
            if (!map.has(runtime)) map.set(runtime, new Map());
            const pipelineMap = map.get(runtime)!;
            if (!pipelineMap.has(pipeline.name)) pipelineMap.set(pipeline.name, []);
            pipelineMap.get(pipeline.name)!.push(lambda);
          };

          if (eolSet.has(lambda.runtime)) addToMap(eolByRuntimeAndPipeline, lambda.runtime);
          if (approachingSet.has(lambda.runtime)) addToMap(approachingByRuntimeAndPipeline, lambda.runtime);
        });
      });
    });

    const convertToArray = (map: Map<string, Map<string, LambdaFunction[]>>) =>
      Array.from(map.entries())
        .map(([runtime, pipelineMap]) => ({
          runtime,
          pipelines: Array.from(pipelineMap.entries())
            .map(([name, lambdas]) => ({ name, lambdas }))
            .sort((a, b) => a.name.localeCompare(b.name)),
          totalCount: Array.from(pipelineMap.values()).reduce((sum, arr) => sum + arr.length, 0)
        }))
        .sort((a, b) => b.totalCount - a.totalCount);

    return {
      eolGroups: convertToArray(eolByRuntimeAndPipeline),
      approachingGroups: convertToArray(approachingByRuntimeAndPipeline),
      approachingCount: Array.from(approachingByRuntimeAndPipeline.values()).reduce(
        (sum, pMap) => sum + Array.from(pMap.values()).reduce((s, arr) => s + arr.length, 0),
        0
      )
    };
  }, [accounts, currentConfig.eolVersions, currentConfig.approachingEolVersions]);

  const eolVersionsSet = useMemo(() => new Set(currentConfig.eolVersions), [currentConfig.eolVersions]);

  const statsData = useMemo(
    () => ({
      totalLambdas: precomputedData.totalLambdas,
      eolCount: precomputedData.eolCount,
      totalPipelines: precomputedData.totalPipelines
    }),
    [precomputedData]
  );

  return {
    // Resource type
    activeResourceType,
    setActiveResourceType,
    enabledConfigs,
    currentConfig,
    // Inventory
    accounts,
    isLoading,
    dataError,
    // Brazil EOL
    brazilEOLData,
    isBrazilEOLLoading,
    brazilEOLError,
    // Precomputed
    precomputedData,
    precomputedEolData,
    eolVersionsSet,
    statsData,
    allLambdas: precomputedData.allLambdas
  };
};
